import { useState } from 'react';

interface JoinTenantFormProps {
  onJoinSuccess: () => void;
  onSwitchToLogin: () => void;
}

const JoinTenantForm: React.FC<JoinTenantFormProps> = ({
  onJoinSuccess,
  onSwitchToLogin,
}) => {
  const [inviteCode, setInviteCode] = useState('');
  const [username, setUsername] = useState('');
  const [customLoginCode, setCustomLoginCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loginCode, setLoginCode] = useState('');
  const [showLoginCode, setShowLoginCode] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim() || !username.trim()) return;

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/join-tenant`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invite_code: inviteCode,
            username: username,
            custom_login_code: customLoginCode.trim() || undefined,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'テナント参加に失敗しました');
      }

      const data = await response.json();
      setLoginCode(data.login_code);
      setShowLoginCode(true);
      setSuccessMessage(
        `アカウントが作成されました！\nログインコード: ${data.login_code}\n\nこのコードを覚えてからログイン画面に進んでください。`
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'テナント参加に失敗しました'
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (showLoginCode) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'var(--neutral-100)',
        }}
      >
        <div
          style={{
            backgroundColor: 'var(--neutral-50)',
            padding: '2rem',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            width: '100%',
            maxWidth: '400px',
            textAlign: 'center',
          }}
        >
          <h2 style={{ color: 'var(--success)', marginBottom: '1rem' }}>
            参加完了！
          </h2>
          <div
            style={{
              whiteSpace: 'pre-line',
              backgroundColor: 'var(--neutral-100)',
              border: '1px solid var(--success)',
              borderRadius: '4px',
              padding: '1rem',
              color: 'var(--success)',
              marginBottom: '1rem',
            }}
          >
            {successMessage}
          </div>
          <div
            style={{
              backgroundColor: 'var(--neutral-100)',
              border: '2px solid var(--success)',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
            }}
          >
            <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--success)' }}>
              ログインコード
            </h3>
            <code
              style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: 'var(--success)',
                backgroundColor: 'var(--neutral-50)',
                padding: '0.5rem',
                borderRadius: '4px',
                border: '1px solid var(--success)',
                display: 'inline-block',
                letterSpacing: '2px',
              }}
            >
              {loginCode}
            </code>
          </div>
          <button
            onClick={onJoinSuccess}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: 'var(--success)',
              color: 'var(--neutral-50)',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1rem',
              cursor: 'pointer',
              marginBottom: '0.5rem',
            }}
          >
            ログイン画面に進む
          </button>
          <p
            style={{
              color: 'var(--neutral-500)',
              fontSize: '0.9rem',
              margin: '0',
            }}
          >
            このコードをメモしてからログイン画面に進んでください
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'var(--neutral-100)',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--neutral-50)',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '400px',
        }}
      >
        <h1
          style={{
            textAlign: 'center',
            marginBottom: '1rem',
            color: 'var(--neutral-800)',
          }}
        >
          みといて
        </h1>
        <h2
          style={{
            textAlign: 'center',
            marginBottom: '1rem',
            color: 'var(--neutral-600)',
            fontSize: '1.2rem',
          }}
        >
          グループに参加
        </h2>
        <p
          style={{
            textAlign: 'center',
            marginBottom: '2rem',
            color: 'var(--neutral-600)',
          }}
        >
          招待コードを使ってグループに参加
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="inviteCode"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: 'var(--neutral-800)',
              }}
            >
              招待コード
            </label>
            <input
              id="inviteCode"
              type="text"
              value={inviteCode}
              onChange={e => setInviteCode(e.target.value)}
              placeholder="招待コードを入力"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--neutral-300)',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
              disabled={isLoading}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="username"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: 'var(--neutral-800)',
              }}
            >
              あなたの名前
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="表示名を入力"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--neutral-300)',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
              disabled={isLoading}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label
              htmlFor="customLoginCode"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: 'var(--neutral-800)',
              }}
            >
              ログインコード（任意）
            </label>
            <input
              id="customLoginCode"
              type="text"
              value={customLoginCode}
              onChange={e => setCustomLoginCode(e.target.value)}
              placeholder="お好みのログインコードを入力（空欄で自動生成）"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--neutral-300)',
                borderRadius: '4px',
                fontSize: '1rem',
                boxSizing: 'border-box',
              }}
              disabled={isLoading}
              maxLength={20}
            />
            <p
              style={{
                fontSize: '0.8rem',
                color: 'var(--neutral-600)',
                margin: '0.25rem 0 0 0',
              }}
            >
              英数字で3〜20文字。空欄の場合は自動生成されます。
            </p>
          </div>

          {error && (
            <div
              style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                backgroundColor: 'var(--neutral-100)',
                border: '1px solid var(--error)',
                borderRadius: '4px',
                color: 'var(--error)',
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !inviteCode.trim() || !username.trim()}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor:
                isLoading || !inviteCode.trim() || !username.trim()
                  ? 'var(--neutral-400)'
                  : 'var(--success)',
              color: 'var(--neutral-50)',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1rem',
              cursor:
                isLoading || !inviteCode.trim() || !username.trim()
                  ? 'not-allowed'
                  : 'pointer',
              marginBottom: '1rem',
            }}
          >
            {isLoading ? '参加中...' : 'グループに参加'}
          </button>

          <button
            type="button"
            onClick={onSwitchToLogin}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: 'transparent',
              color: 'var(--info)',
              border: '1px solid var(--info)',
              borderRadius: '4px',
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            すでにアカウントをお持ちですか？ログイン
          </button>
        </form>
      </div>
    </div>
  );
};

export default JoinTenantForm;
